/**
 * Sidebar Navigation Component
 * Styled to match R4S Macula design
 * Includes role-based menu visibility
 */

import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Database,
  Settings,
  FileJson,
  Plug,
  ChevronLeft,
  Menu,
  Brain,
  BarChart3,
  Settings2,
  Zap,
  GanttChart,
} from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';
import { useAuth, useAppSettings } from '../../contexts';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    title: 'MAIN',
    items: [
      { name: 'Dashboards', href: '/', icon: LayoutDashboard },
      { name: 'AI Planner', href: '/planning', icon: Brain },
      { name: 'KPI Dashboard', href: '/kpi', icon: BarChart3 },
      { name: 'Machines', href: '/machines', icon: Settings2 },
      { name: 'Scheduling', href: '/scheduling', icon: Zap },
      { name: 'Gantt Chart', href: '/gantt', icon: GanttChart },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { name: 'Endpoints', href: '/endpoints', icon: Database, adminOnly: true },
      { name: 'Connections', href: '/connections', icon: Plug, adminOnly: true },
      { name: 'Templates', href: '/templates', icon: FileJson, adminOnly: true },
      { name: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
    ],
  },
];

function DefaultLogoSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 79.73 100" className={className}>
      <path fill="#45bdd6" d="M39.71 0L0 99.94l55.6-29.78L39.71 0z"/>
      <path fill="#674EA7" d="M40.02.06L79.73 100l-55.6-29.78L40.02.06z"/>
      <path fill="#43434f" d="M39.87.7L24.13 70.22l15.68 8.4 15.79-8.46L39.87.7z"/>
    </svg>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { isAdmin } = useAuth();
  const { appName, logoDataUrl } = useAppSettings();

  return (
    <aside
      className={clsx(
        'flex flex-col bg-[#1e1e2d] text-white transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo & Hamburger */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-[#2d2d3d]">
        {!collapsed ? (
          <>
            <div className="flex items-center gap-3 min-w-0">
              {/* Logo — custom or default R4S */}
              <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                {logoDataUrl ? (
                  <img src={logoDataUrl} alt="Logo" className="w-7 h-7 object-contain rounded" />
                ) : (
                  <DefaultLogoSvg className="w-6 h-6" />
                )}
              </div>
              <span className="font-semibold text-sm text-white truncate">{appName || 'R4S OmniMens'}</span>
            </div>
            <button
              onClick={() => setCollapsed(true)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-[#2d2d3d] rounded transition-colors flex-shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setCollapsed(false)}
            className="w-full flex items-center justify-center p-1.5 text-gray-400 hover:text-white hover:bg-[#2d2d3d] rounded transition-colors"
          >
            {logoDataUrl ? (
              <img src={logoDataUrl} alt="Logo" className="w-6 h-6 object-contain rounded" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {navigation.map((section) => {
          // Filter items based on role
          const visibleItems = section.items.filter((item) => !item.adminOnly || isAdmin);

          // Don't render section if no visible items
          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title} className="mb-6">
              {!collapsed && (
                <h3 className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {section.title}
                </h3>
              )}
              <ul className="space-y-1 px-2">
                {visibleItems.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        clsx(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                          isActive
                            ? 'bg-[#45bdd6] text-white'
                            : 'text-gray-400 hover:bg-[#2d2d3d] hover:text-white'
                        )
                      }
                      title={collapsed ? item.name : undefined}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {!collapsed && <span className="text-sm">{item.name}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* Collapse Button */}
      <div className="p-2 border-t border-[#2d2d3d]">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-gray-500 hover:text-white hover:bg-[#2d2d3d] rounded-lg transition-colors text-sm"
        >
          {collapsed ? (
            <ChevronLeft className="w-5 h-5 rotate-180" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
